/**
 * Página de Registro — Wizard 2 pasos
 * Step 1: Datos personales / contacto
 * Step 2: Nombre del restaurante + T&C (mínimo — NER-4185)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { resolvePreselectedPlanId } from '../utils/planPreselection';
import { trackPixel } from '../utils/analytics';
import { getPlans, createRestaurant, checkSlugAvailability } from '../services/api';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { storage } from '../utils/storage';
import './AuthPages.css';

const MAX_CONFIRM_ATTEMPTS = 5;
const LOCKOUT_DURATION = 30;

// CUIT/CUIL argentino: 11 dígitos. Validación de formato (no de dígito
// verificador — la validación fiscal estricta vive en Configuración Fiscal).
// (NER-4257 follow-up: unificado acá desde el OnboardingWizard, a pedido de
// Diego — se pide junto con el resto de los datos del alta, no en una
// pantalla aparte después del login.)
const isValidCuitFormat = (cuit) => /^\d{11}$/.test(cuit.replace(/\D/g, ''));

function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const {
    user,
    register,
    confirmRegistration,
    resendConfirmationCode,
    login,
    createOrganizationAfterSignup,
    checkUser,
    authLoading,
  } = useAuth();

  const [step, setStep] = useState('account-type'); // 'account-type', 'step1', 'step2', 'confirm', 'creating-org'
  const [accountType, setAccountType] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    address: '',
    city: '',
    province: '',
    country: 'Argentina',
    postalCode: '',
    phone: '',
    companyName: '',
    restaurantName: '',
    restaurantCoords: null,
    cuit: '',
    acceptTerms: false,
  });
  const [confirmationCode, setConfirmationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Hallazgo QA 2026-09-10 (ronda 3): chequeo de disponibilidad del nombre
  // del restaurante ANTES de crear la cuenta — mismo problema que en el gate
  // de Google (CreatingOrgPage): un nombre/slug ya usado por otro restaurante
  // hacía fallar en silencio la creación best-effort post-confirmación.
  const [nameAvailability, setNameAvailability] = useState('idle');
  const nameCheckTimer = useRef(null);
  const nameCheckSeq = useRef(0);
  const [cuitError, setCuitError] = useState('');

  // Rate limiting para confirmación
  const [confirmAttempts, setConfirmAttempts] = useState(0);
  const [lockoutSeconds, setLockoutSeconds] = useState(0);

  // Reenvío de código de confirmación
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendMessage, setResendMessage] = useState(null); // { type: 'success'|'error', text }

  // Auto-generate slug from restaurant name
  const generateSlug = useCallback((name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);
  }, []);

  // Leer ?plan=X de la landing y persistir para el OnboardingWizard.
  // sessionStorage sobrevive el re-mount del ciclo de confirmación de Cognito,
  // sin persistir entre sesiones (a diferencia de localStorage).
  useEffect(() => {
    const planId = resolvePreselectedPlanId(searchParams.get('plan'));
    if (planId) {
      sessionStorage.setItem('preselectedPlan', planId);
    }
  }, [searchParams]);

  // NER-3781/NER-3807: el flujo Google admin (CreatingOrgPage) rutea acá con
  // ?error=... cuando complete-signup falla de forma recuperable en registro.
  useEffect(() => {
    const flowError = searchParams.get('error');
    if (flowError === 'user_already_exists') {
      setError(t('register.errors.googleExists'));
    } else if (flowError === 'token_invalid') {
      setError(t('register.errors.googleTokenInvalid'));
    }
  }, [searchParams, t]);

  // Redirigir si ya está autenticado
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  // Countdown del lockout
  useEffect(() => {
    if (lockoutSeconds <= 0) return;
    const timer = setTimeout(() => setLockoutSeconds((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [lockoutSeconds]);

  // Countdown del reenvío de código
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Hallazgo QA 2026-09-10 (ronda 3): mismo chequeo de disponibilidad que
  // CreatingOrgPage (gate de Google) — debounced, endpoint público
  // /restaurants/public/resolve-slug-global. Ver ese archivo para el detalle
  // completo del hallazgo (creación best-effort fallando en silencio).
  useEffect(() => {
    const trimmed = formData.restaurantName.trim();
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);

    if (!trimmed || step !== 'step2') {
      setNameAvailability('idle');
      return undefined;
    }

    const slug = formData.restaurantSlug || generateSlug(trimmed);
    if (!slug) {
      setNameAvailability('idle');
      return undefined;
    }

    setNameAvailability('checking');
    const seq = ++nameCheckSeq.current;
    nameCheckTimer.current = setTimeout(async () => {
      try {
        const available = await checkSlugAvailability(slug);
        if (seq !== nameCheckSeq.current) return;
        setNameAvailability(available ? 'available' : 'taken');
        if (!available) setError(t('register.errors.restaurantNameTaken'));
      } catch (checkErr) {
        if (seq === nameCheckSeq.current) setNameAvailability('idle');
      }
    }, 500);

    return () => clearTimeout(nameCheckTimer.current);
  }, [formData.restaurantName, formData.restaurantSlug, step, generateSlug, t]);

  const getLockoutDuration = useCallback((attempts) => {
    if (attempts >= 10) return 300;
    if (attempts >= MAX_CONFIRM_ATTEMPTS) return LOCKOUT_DURATION;
    return 0;
  }, []);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: fieldValue };
      if (name === 'restaurantName') {
        next.restaurantSlug = generateSlug(value);
      }
      return next;
    });
    setError('');
  };

  const handleCuitChange = (e) => {
    let val = e.target.value.replace(/\D/g, '').slice(0, 11);
    if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
    if (val.length > 10) val = val.slice(0, 11) + '-' + val.slice(11);
    setFormData((prev) => ({ ...prev, cuit: val }));
    setCuitError('');
  };

  const validatePassword = (password) => {
    if (password.length < 8) return t('register.errors.passwordLength');
    if (!/[A-Z]/.test(password)) return t('register.errors.passwordUppercase');
    if (!/[a-z]/.test(password)) return t('register.errors.passwordLowercase');
    if (!/\d/.test(password)) return t('register.errors.passwordNumber');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) return t('register.errors.passwordSpecial');
    return null;
  };

  // NER-4257 follow-up (roadmap QA 2026-09-10 #1): el telefono es opcional,
  // pero si se completa Cognito exige formato E.164 estricto (+ y solo
  // digitos). Antes esto solo se detectaba recien al hacer "Crear Cuenta" en
  // el Step 2, obligando a volver atras al Step 1 para corregirlo. Ahora se
  // normaliza (se sacan espacios/guiones/parentesis) y se valida en el Step 1.
  const normalizePhone = (phone) => phone.replace(/[\s\-().]/g, '');

  const isValidE164Phone = (phone) => /^\+[1-9]\d{7,14}$/.test(phone);

  // Step 1 validation
  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password) {
      setError(t('register.errors.requiredFields'));
      return false;
    }

    if (accountType === 'company' && !formData.companyName) {
      setError(t('register.errors.companyNameRequired'));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return false;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return false;
    }

    if (formData.phone && formData.phone.trim()) {
      const normalizedPhone = normalizePhone(formData.phone.trim());
      if (!isValidE164Phone(normalizedPhone)) {
        setError(t('register.errors.phoneFormat'));
        return false;
      }
    }

    return true;
  };

  const handleNextStep = () => {
    setError('');
    if (validateStep1()) {
      setStep('step2');
    }
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!formData.restaurantName.trim()) {
      setError(t('register.errors.restaurantNameRequired'));
      setLoading(false);
      return;
    }

    if (!formData.address.trim()) {
      setError(t('register.errors.restaurantAddressRequired'));
      setLoading(false);
      return;
    }

    const cuitTrimmed = formData.cuit.trim();
    if (cuitTrimmed && !isValidCuitFormat(cuitTrimmed)) {
      setCuitError(t('register.errors.cuitInvalid'));
      setLoading(false);
      return;
    }

    if (!formData.acceptTerms) {
      setError(t('register.errors.acceptTerms'));
      setLoading(false);
      return;
    }

    // Hallazgo QA 2026-09-10 (ronda 3): re-chequeo sincrónico justo antes de
    // enviar (no confiar solo en el debounced de fondo, que puede no haber
    // terminado si el usuario completó el form muy rápido).
    const restaurantSlugToCheck = formData.restaurantSlug || generateSlug(formData.restaurantName);
    if (nameAvailability === 'taken') {
      setError(t('register.errors.restaurantNameTaken'));
      setLoading(false);
      return;
    }
    try {
      const available = await checkSlugAvailability(restaurantSlugToCheck);
      if (!available) {
        setNameAvailability('taken');
        setError(t('register.errors.restaurantNameTaken'));
        setLoading(false);
        return;
      }
    } catch (availabilityErr) {
      // Si el chequeo mismo falla (red/5xx), no bloqueamos el registro —
      // createRestaurant server-side vuelve a validar unicidad igual.
      console.warn('[RegisterPage] No se pudo verificar disponibilidad del nombre:', availabilityErr.message);
    }

    try {
      const fullName = `${formData.firstName} ${formData.lastName}`;
      const restaurantSlug = formData.restaurantSlug || generateSlug(formData.restaurantName);
      const userAttributes = {
        phone: formData.phone && formData.phone.trim() ? normalizePhone(formData.phone.trim()) : '',
        // NER-4257 follow-up: el CUIT vuelve a pedirse acá (unificado con el
        // resto del alta, a pedido de Diego) en vez del OnboardingWizard
        // post-login. Sigue siendo opcional — se puede completar después en
        // Configuración Fiscal, que es la fuente autoritativa para facturación.
        cuit: cuitTrimmed ? cuitTrimmed.replace(/\D/g, '') : '',
        address: formData.address,
        city: formData.city,
        province: formData.province,
        country: formData.country,
        postalCode: formData.postalCode,
        accountType: accountType,
        restaurantName: formData.restaurantName,
        restaurantSlug: restaurantSlug,
      };

      if (accountType === 'company') {
        userAttributes.companyName = formData.companyName;
        userAttributes.organizationName = formData.companyName;
      } else {
        userAttributes.organizationName = t('register.defaultOrganizationName');
      }

      const result = await register(formData.email, formData.password, fullName, userAttributes);

      if (result.success) {
        // NER-3217: conversión Lead post submit del form de registro.
        // Mismo pixel que la landing (cross-domain) — no-op sin consentimiento.
        trackPixel('Lead', {
          content_name: 'resto_admin_register',
          content_category: sessionStorage.getItem('preselectedPlan') || 'none',
        });
        setStep('confirm');
      } else {
        setError(result.error || t('register.errors.registerError'));
      }
    } catch (err) {
      console.error('Error en registro:', err);
      setError(t('register.errors.registerError'));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || resendLoading) return;

    setResendLoading(true);
    setResendMessage(null);

    try {
      const result = await resendConfirmationCode(formData.email);
      if (result.success) {
        setResendMessage({ type: 'success', text: t('register.resendSuccess') });
        setResendCooldown(30);
      } else {
        setResendMessage({
          type: 'error',
          text: result.error || t('register.resendError'),
        });
      }
    } catch (err) {
      console.error('Error reenviando código:', err);
      setResendMessage({ type: 'error', text: t('register.resendError') });
    } finally {
      setResendLoading(false);
    }
  };

  const handleConfirmSubmit = async (e) => {
    e.preventDefault();

    if (lockoutSeconds > 0) return;

    setError('');
    setLoading(true);

    if (!confirmationCode) {
      setError(t('register.errors.confirmationCodeRequired'));
      setLoading(false);
      return;
    }

    try {
      const confirmResult = await confirmRegistration(formData.email, confirmationCode);

      if (!confirmResult.success) {
        const newAttempts = confirmAttempts + 1;
        setConfirmAttempts(newAttempts);
        const lockDuration = getLockoutDuration(newAttempts);
        if (lockDuration > 0) {
          setLockoutSeconds(lockDuration);
          setError(t('login.errors.tooManyAttempts', { seconds: lockDuration }));
        } else {
          setError(confirmResult.error || t('register.errors.confirmError'));
        }
        setLoading(false);
        return;
      }

      // NER-3217: CompleteRegistration post Cognito confirm success.
      // value = precio mensual ARS del plan preseleccionado (best-effort:
      // si getPlans falla o no hay plan, se emite sin value).
      try {
        const planId = sessionStorage.getItem('preselectedPlan');
        const pixelParams = { content_name: 'resto_admin_register' };
        if (planId) {
          pixelParams.content_category = planId;
          const plansResponse = await getPlans();
          const plans = plansResponse?.plans || plansResponse || [];
          const plan = Array.isArray(plans) ? plans.find((p) => p.planId === planId) : null;
          const monthly = plan?.pricing?.ARS?.monthly;
          if (typeof monthly === 'number' && monthly > 0) {
            pixelParams.value = monthly;
            pixelParams.currency = 'ARS';
          }
        }
        trackPixel('CompleteRegistration', pixelParams);
      } catch (pixelErr) {
        // Analytics nunca bloquea el flujo de registro
        console.warn('Pixel CompleteRegistration no enviado:', pixelErr);
      }

      setStep('creating-org');
      const loginResult = await login(formData.email, formData.password);

      if (!loginResult.success) {
        navigate('/login', {
          state: {
            message: t('register.accountConfirmedLogin'),
            email: formData.email,
          },
        });
        return;
      }

      const orgResult = await createOrganizationAfterSignup();

      if (!orgResult.success) {
        console.error('Error al crear organización:', orgResult.error);
        setError(t('register.errors.orgCreationError'));
        setLoading(false);
        return;
      }

      // Force token refresh so Cognito groups from org creation are included.
      // checkUser también sincroniza selectedOrganizationId en storage, necesario
      // para que createRestaurant envíe el header X-Organization-Id correcto.
      await checkUser(true);

      // NER-4257: crear el primer restaurante con el nombre + dirección recolectados
      // en el wizard. Así el guard RequireRestaurant lo encuentra y el usuario aterriza
      // directo en el dashboard, en vez de la RestaurantFormPage (6 tabs con warnings)
      // que estaba generando abandono (0 usuarios productivos post Meta ads).
      // Best-effort: si falla (p.ej. slug global duplicado o error de red), NO se
      // rompe el signup — RequireRestaurant lo mandará a /restaurant/new como antes.
      try {
        const restaurantSlug = formData.restaurantSlug || generateSlug(formData.restaurantName);
        const createResponse = await createRestaurant({
          name: formData.restaurantName.trim(),
          slug: restaurantSlug,
          address: formData.address.trim(),
          ...(formData.restaurantCoords
            ? {
                location: {
                  lat: String(formData.restaurantCoords.lat),
                  lng: String(formData.restaurantCoords.lng),
                },
              }
            : {}),
        });
        const newRestaurantId =
          createResponse?.restaurantId || createResponse?.restaurant?.restaurantId;
        if (newRestaurantId) {
          storage.set('selectedRestaurantId', newRestaurantId);
          storage.set('selectedRestaurantName', formData.restaurantName.trim());
        }
      } catch (restErr) {
        console.warn(
          'No se pudo crear el restaurante inicial post-signup; el usuario lo completará en /restaurant/new',
          restErr
        );
      }

      navigate('/', {
        state: { message: t('register.welcomeMessage') },
      });
    } catch (err) {
      console.error('Error en el proceso de confirmación:', err);
      setError(t('register.errors.confirmError'));
      setLoading(false);
    }
  };

  const handleCardKeyDown = (e, type) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setAccountType(type);
      setStep('step1');
    }
  };

  // Step indicator for wizard steps
  const getWizardStepNumber = () => {
    if (step === 'step1') return 1;
    if (step === 'step2') return 2;
    return null;
  };

  const wizardStep = getWizardStepNumber();

  if (authLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Resto</h1>
          <h2>
            {step === 'account-type' && t('register.selectAccountType')}
            {step === 'step1' && t('register.step1Title')}
            {step === 'step2' && t('register.step2Title')}
            {step === 'confirm' && t('register.confirmAccount')}
            {step === 'creating-org' && t('register.settingUpAccount')}
          </h2>
        </div>

        {/* Step indicator */}
        {wizardStep && (
          <div className="register-steps">
            <div className={`register-step ${wizardStep >= 1 ? 'register-step--active' : ''}`}>
              <span className="register-step__number">1</span>
              <span className="register-step__label">{t('register.step1Label')}</span>
            </div>
            <div className="register-step__connector" />
            <div className={`register-step ${wizardStep >= 2 ? 'register-step--active' : ''}`}>
              <span className="register-step__number">2</span>
              <span className="register-step__label">{t('register.step2Label')}</span>
            </div>
          </div>
        )}

        {step === 'creating-org' ? (
          <div className="auth-form">
            <div className="loading-message">
              <p>{t('register.settingUpMessage')}</p>
              <p>{t('register.pleaseWait')}</p>
            </div>
          </div>
        ) : step === 'account-type' ? (
          <div className="auth-form">
            {/* NER-3781: Google Sign-In como opción principal (1-click). Se
                auto-oculta si el OAuth federado aún no está aprovisionado. */}
            <GoogleSignInButton context="register" />

            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
              <div
                role="button"
                tabIndex={0}
                aria-label={t('register.selectPerson')}
                onClick={() => {
                  setAccountType('person');
                  setStep('step1');
                }}
                onKeyDown={(e) => handleCardKeyDown(e, 'person')}
                style={{
                  flex: 1,
                  padding: '30px',
                  border: '2px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>👤</div>
                <h3 style={{ margin: '0 0 10px 0' }}>{t('register.person')}</h3>
                <p
                  style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}
                >
                  {t('register.personDescription')}
                </p>
              </div>

              <div
                role="button"
                tabIndex={0}
                aria-label={t('register.selectCompany')}
                onClick={() => {
                  setAccountType('company');
                  setStep('step1');
                }}
                onKeyDown={(e) => handleCardKeyDown(e, 'company')}
                style={{
                  flex: 1,
                  padding: '30px',
                  border: '2px solid var(--border-color)',
                  borderRadius: 'var(--radius-md)',
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-primary)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-color)';
                }}
              >
                <div style={{ fontSize: '48px', marginBottom: '15px' }}>🏢</div>
                <h3 style={{ margin: '0 0 10px 0' }}>{t('register.company')}</h3>
                <p
                  style={{ margin: 0, color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}
                >
                  {t('register.companyDescription')}
                </p>
              </div>
            </div>

            <div className="auth-footer">
              <p>
                {t('register.alreadyHaveAccount')}{' '}
                <Link to="/login">{t('register.loginHere')}</Link>
              </p>
            </div>
          </div>
        ) : step === 'step1' ? (
          /* ========== STEP 1: Datos personales / contacto ========== */
          <div className="auth-form">
            {/* Tipo de cuenta seleccionado */}
            <div
              style={{
                marginBottom: '4px',
                padding: '10px',
                backgroundColor: 'var(--bg-secondary)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              <strong>{t('register.accountType')}:</strong>{' '}
              {accountType === 'person' ? t('register.person') : t('register.company')}{' '}
              <button
                type="button"
                onClick={() => setStep('account-type')}
                style={{
                  marginLeft: '10px',
                  color: 'var(--color-primary)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {t('register.change')}
              </button>
            </div>

            {/* Nombre y Apellido */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label htmlFor="firstName">{t('register.firstName')} *</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder={t('register.firstNamePlaceholder')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="lastName">{t('register.lastName')} *</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder={t('register.lastNamePlaceholder')}
                />
              </div>
            </div>

            {/* Email */}
            <div className="form-group">
              <label htmlFor="email">{t('register.email')} *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('register.emailPlaceholder')}
                autoComplete="email"
              />
            </div>

            {/* Contraseñas */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div className="form-group">
                <label htmlFor="password">{t('register.password')} *</label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
                <small className="form-help">{t('register.passwordHelp')}</small>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">{t('register.confirmPassword')} *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
            </div>

            {/* Teléfono */}
            <div className="form-group">
              <label htmlFor="phone">
                {t('register.phone')}{' '}
                <span
                  style={{
                    color: 'var(--text-tertiary)',
                    fontWeight: 400,
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  ({t('register.optional')})
                </span>
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+54 11 1234-5678"
                inputMode="tel"
              />
            </div>

            {/* Razón Social (solo para empresa) */}
            {accountType === 'company' && (
              <div className="form-group">
                <label htmlFor="companyName">{t('register.companyName')} *</label>
                <input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder={t('register.companyNamePlaceholder')}
                />
              </div>
            )}

            <p style={{ fontSize: 'var(--text-xs)', color: 'var(--text-tertiary)', margin: '0' }}>
              * {t('register.requiredFields')}
            </p>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <button type="button" className="btn btn-primary btn-block" onClick={handleNextStep}>
              {t('register.next')}
            </button>
          </div>
        ) : step === 'step2' ? (
          /* ========== STEP 2: Nombre del restaurante + T&C (mínimo) ========== */
          <form onSubmit={handleRegisterSubmit} className="auth-form" noValidate>
            {/*
             * NER-4185: Step 2 reducido al mínimo para bajar la barrera de entrada.
             * Se eliminaron país/provincia/ciudad/dirección/código postal del wizard
             * (eran opcionales y sólo agregaban fricción). El usuario los completa
             * luego en Ajustes → "Completar perfil". Los campos siguen en formData
             * como '' y se envían vacíos al backend (createOrganizationAfterSignup
             * los tolera).
             */}
            {/* Nombre del restaurante */}
            <div className="form-group">
              <label htmlFor="restaurantName">{t('register.restaurantName')} *</label>
              <input
                type="text"
                id="restaurantName"
                name="restaurantName"
                value={formData.restaurantName}
                onChange={handleChange}
                placeholder={t('register.restaurantNamePlaceholder')}
              />
              {formData.restaurantName && nameAvailability !== 'checking' && (
                <small className="form-help">
                  {t('register.restaurantSlugPreview')}: {generateSlug(formData.restaurantName)}
                </small>
              )}
              {nameAvailability === 'checking' && (
                <small className="form-help">{t('register.checkingAvailability')}</small>
              )}
            </div>

            {/* Dirección del restaurante (NER-4257): con Google Places Autocomplete.
                Se usa para crear el primer restaurante en el post-signup y aterrizar
                directo en el dashboard, evitando la RestaurantFormPage intimidante. */}
            <div className="form-group">
              <label htmlFor="restaurantAddress">{t('register.restaurantAddress')} *</label>
              <AddressAutocomplete
                id="restaurantAddress"
                name="address"
                value={formData.address}
                placeholder={t('register.restaurantAddressPlaceholder')}
                onChange={(address) => setFormData((prev) => ({ ...prev, address }))}
                onPlaceSelected={({ address, coords }) =>
                  setFormData((prev) => ({
                    ...prev,
                    address: address || prev.address,
                    restaurantCoords: coords || null,
                  }))
                }
              />
            </div>

            {/* CUIT/CUIL (NER-4257 follow-up): unificado acá desde el
                OnboardingWizard post-login, a pedido de Diego. Opcional — se
                puede completar después en Configuración Fiscal. */}
            <div className="form-group">
              <label htmlFor="cuit">{t('register.cuitLabel')}</label>
              <input
                type="text"
                id="cuit"
                name="cuit"
                inputMode="numeric"
                value={formData.cuit}
                placeholder="20-12345678-9"
                onChange={handleCuitChange}
              />
              {cuitError && <div className="error-message">{cuitError}</div>}
              <small className="form-help">{t('register.cuitHint')}</small>
            </div>

            {/* Términos y condiciones */}
            <div className="form-group-checkbox">
              <label>
                <input
                  type="checkbox"
                  name="acceptTerms"
                  checked={formData.acceptTerms}
                  onChange={handleChange}
                />
                <span>
                  {t('register.acceptTerms')}{' '}
                  <Link to="/terms" target="_blank">
                    {t('register.termsLink')}
                  </Link>
                </span>
              </label>
            </div>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => {
                  setStep('step1');
                  setError('');
                }}
                style={{ flex: 1 }}
              >
                {t('register.back')}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{ flex: 2 }}
              >
                {loading ? t('register.registering') : t('register.createAccount')}
              </button>
            </div>
          </form>
        ) : step === 'confirm' ? (
          <form onSubmit={handleConfirmSubmit} className="auth-form" noValidate>
            <p className="confirmation-message">
              {t('register.confirmationSent')} <strong>{formData.email}</strong>.{' '}
              {t('register.enterCode')}
            </p>

            <div className="form-group">
              <label htmlFor="confirmationCode">{t('register.confirmationCode')}</label>
              <input
                type="text"
                id="confirmationCode"
                value={confirmationCode}
                onChange={(e) => {
                  setConfirmationCode(e.target.value);
                  setError('');
                }}
                placeholder="123456"
                maxLength="6"
                inputMode="numeric"
                disabled={lockoutSeconds > 0}
              />
            </div>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            {resendMessage && (
              <div
                className={
                  resendMessage.type === 'success' ? 'success-message' : 'error-message'
                }
                role="alert"
                aria-live="polite"
              >
                {resendMessage.text}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary btn-block"
              disabled={loading || lockoutSeconds > 0}
            >
              {loading
                ? t('register.confirming')
                : lockoutSeconds > 0
                  ? t('login.lockedOut', { seconds: lockoutSeconds })
                  : t('register.confirmAccount')}
            </button>

            <button
              type="button"
              className="btn btn-link btn-block"
              onClick={handleResendCode}
              disabled={resendLoading || resendCooldown > 0}
            >
              {resendLoading
                ? t('register.resending')
                : resendCooldown > 0
                  ? t('register.resendCodeCooldown', { seconds: resendCooldown })
                  : t('register.resendCode')}
            </button>

            <button
              type="button"
              className="btn btn-ghost btn-block"
              onClick={() => {
                setStep('step2');
                setError('');
              }}
            >
              {t('common.back')}
            </button>
          </form>
        ) : null}

        {step !== 'account-type' && (
          <div className="auth-footer">
            <p>
              {t('register.alreadyHaveAccount')} <Link to="/login">{t('register.loginHere')}</Link>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RegisterPage;
